import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { pathIsAllowed, firstAllowedPath } from '@/lib/permissions'
import { MainLayout } from '@/components/layout/MainLayout'
import { Dashboard } from '@/pages/Dashboard'
import { Pipeline } from '@/pages/Pipeline'
import { Login } from '@/pages/Login'
import Setup2FAPage from '@/pages/Setup2FAPage'
import SalesDataRoomPage from './pages/SalesDataRoomPage'
import SalesDataRoomAdminPage from './pages/SalesDataRoomAdminPage'
import { UsersPage } from '@/pages/UsersPage'
import { LeadsPage } from '@/pages/LeadsPage'
import { OpportunitiesPage } from '@/pages/OpportunitiesPage'
import { OpportunityDetailPage } from '@/pages/OpportunityDetailPage'
import RdcPage from '@/pages/RdcPage'
import RdcFormPage from '@/pages/RdcFormPage'
import RdcAceitePage from '@/pages/RdcAceitePage'
import PartnerReportsPage from '@/pages/PartnerReportsPage'
import DiagnosticFormPage from '@/pages/DiagnosticFormPage'
import AssessmentIGAPage from '@/pages/AssessmentIGAPage'
import AssessmentsHubPage from '@/pages/AssessmentsHubPage'
import AssessmentIGAResultPage from '@/pages/AssessmentIGAResultPage'
import AssessmentBS4ITPage from '@/pages/AssessmentBS4ITPage'
import AssessmentBS4ITResultPage from '@/pages/AssessmentBS4ITResultPage'
import AssessmentPrivacyPage from '@/pages/AssessmentPrivacyPage'
import AssessmentPrivacyResultPage from '@/pages/AssessmentPrivacyResultPage'
import AssessmentValueChainPage from '@/pages/AssessmentValueChainPage'
import AssessmentValueChainResultPage from '@/pages/AssessmentValueChainResultPage'
import AssessmentsAdminPage from '@/pages/AssessmentsAdminPage'
import DiagnosticResultPage from '@/pages/DiagnosticResultPage'
import CampaignsPage from '@/pages/CampaignsPage'
import CampaignDashboardPage from '@/pages/CampaignDashboardPage'
import ProposalsPage from '@/pages/ProposalsPage'
import ProposalSharePage from '@/pages/ProposalSharePage'
import ContentHubPage from '@/pages/ContentHubPage'
import AnalyticsPage from '@/pages/AnalyticsPage'
import DmarcPage from '@/pages/DmarcPage'
import SettingsPage from '@/pages/SettingsPage'
import ProfilePage from '@/pages/ProfilePage'
import CatalogPage from '@/pages/CatalogPage'
import PriceTablesPage from '@/pages/PriceTablesPage'
import OutreachPage from '@/pages/OutreachPage'
import EmailMarketingPage from '@/pages/EmailMarketingPage'
import EmTemplatesPage from '@/pages/EmTemplatesPage'
import EmListsPage from '@/pages/EmListsPage'
import EmAccountsPage from '@/pages/EmAccountsPage'
import CadencesPage from '@/pages/CadencesPage'
import CsvImportPage from '@/pages/CsvImportPage'
import MarketAnalysisPage from "@/pages/MarketAnalysisPage"
import MQLDashboardPage from "@/pages/MQLDashboardPage"
import CustomerSuccessPage from "@/pages/CustomerSuccessPage"
import ClientsPage from "@/pages/ClientsPage"
import NpsRespondPage from "@/pages/NpsRespondPage"
import NaiDeskPage from '@/pages/nai-desk/NaiDeskPage'
import NaiDeskTicketsAdmin from '@/pages/nai-desk/admin/TicketsAdmin'
import NaiDeskTicketDetail from '@/pages/nai-desk/admin/TicketDetailAdmin'
import NaiDeskMelhoriasAdmin from '@/pages/nai-desk/admin/MelhoriasAdmin'
import NaiDeskFaqAdmin from '@/pages/nai-desk/admin/FaqAdmin'
import NaiDeskEmailTemplatesAdmin from '@/pages/nai-desk/admin/EmailTemplatesAdmin'
import NaiDeskSettingsAdmin from '@/pages/nai-desk/admin/SettingsAdmin'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function ModuleGuard({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const location = useLocation()
  if (!pathIsAllowed(user, location.pathname)) {
    const fallback = firstAllowedPath(user)
    if (fallback && fallback !== location.pathname) {
      return <Navigate to={fallback} replace />
    }
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-xl font-semibold">Sem acesso</h1>
          <p className="text-sm text-muted-foreground">
            Seu usuário não tem permissão para acessar nenhum módulo. Entre em contato com um administrador.
          </p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

export default function App() {
  const theme = useUIStore((state) => state.theme)

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      root.classList.toggle('dark', systemTheme === 'dark')
    } else {
      root.classList.toggle('dark', theme === 'dark')
    }
  }, [theme])

  return (
    <Routes>
      {/* Public routes - no auth required */}
      <Route path="/diagnostico-esg" element={<DiagnosticFormPage />} />
      <Route path="/diagnostico-esg/resultado/:id" element={<DiagnosticResultPage />} />
      <Route path="/assessments" element={<AssessmentsHubPage />} />
      <Route path="/assessment/iga" element={<AssessmentIGAPage />} />
      <Route path="/assessment/iga/resultado/:id" element={<AssessmentIGAResultPage />} />
      <Route path="/assessment/bs4it" element={<AssessmentBS4ITPage />} />
      <Route path="/assessment/bs4it/resultado/:id" element={<AssessmentBS4ITResultPage />} />
      <Route path="/assessment/privacy" element={<AssessmentPrivacyPage />} />
      <Route path="/assessment/privacy/resultado/:id" element={<AssessmentPrivacyResultPage />} />
      <Route path="/assessment/cadeia-de-valor" element={<AssessmentValueChainPage />} />
      <Route path="/assessment/cadeia-de-valor/resultado/:id" element={<AssessmentValueChainResultPage />} />
      <Route path="/respond/:token" element={<NpsRespondPage />} />
      {/* de acordo do RDC: publico, o token do e-mail e a credencial */}
      <Route path="/rdc/aceite/:token" element={<RdcAceitePage />} />

      <Route path="/login" element={<Login />} />
          <Route path="/setup-2fa" element={<Setup2FAPage />} />
      <Route path="/" element={<ProtectedRoute><ModuleGuard><MainLayout /></ModuleGuard></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="proposals" element={<ProposalsPage />} />
        <Route path="proposals/:id/share" element={<ProposalSharePage />} />
        <Route path="products" element={<CatalogPage />} />
        <Route path="price-tables" element={<PriceTablesPage />} />
        <Route path="content" element={<ContentHubPage />} />
        <Route path="social" element={<ContentHubPage />} />
        <Route path="opportunities" element={<OpportunitiesPage />} />
        <Route path="opportunities/:id" element={<OpportunityDetailPage />} />
        <Route path="rdc" element={<RdcPage />} />
        <Route path="partner-reports" element={<PartnerReportsPage />} />
        <Route path="rdc/novo" element={<RdcFormPage />} />
        <Route path="rdc/:id" element={<RdcFormPage />} />
        <Route path="campaigns" element={<CampaignsPage />} />
        <Route path="campaigns/:id" element={<CampaignDashboardPage />} />
        <Route path="email-marketing" element={<EmailMarketingPage />} />
        <Route path="email-marketing/templates" element={<EmTemplatesPage />} />
        <Route path="email-marketing/lists" element={<EmListsPage />} />
        <Route path="email-marketing/accounts" element={<EmAccountsPage />} />
        <Route path="outreach" element={<OutreachPage />} />
        <Route path="outreach/cadences" element={<CadencesPage />} />
        <Route path="outreach/csv-import" element={<CsvImportPage />} />
        <Route path="marketing/market-analysis" element={<MarketAnalysisPage />} />
        <Route path="marketing/mql-dashboard" element={<MQLDashboardPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="analytics/dmarc" element={<DmarcPage />} />
        <Route path="cs" element={<CustomerSuccessPage />} />
        <Route path="cs/clients" element={<ClientsPage />} />
        <Route path="cs/campaigns" element={<CustomerSuccessPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="sales-dataroom" element={<SalesDataRoomPage />} />
        <Route path="sales-dataroom/admin" element={<SalesDataRoomAdminPage />} />
        <Route path="assessments-admin" element={<AssessmentsAdminPage />} />
        <Route path="nai-desk" element={<NaiDeskPage />} />
        <Route path="nai-desk/admin/tickets" element={<NaiDeskTicketsAdmin />} />
        <Route path="nai-desk/admin/tickets/:id" element={<NaiDeskTicketDetail />} />
        <Route path="nai-desk/admin/melhorias" element={<NaiDeskMelhoriasAdmin />} />
        <Route path="nai-desk/admin/faq" element={<NaiDeskFaqAdmin />} />
        <Route path="nai-desk/admin/email-templates" element={<NaiDeskEmailTemplatesAdmin />} />
        <Route path="nai-desk/admin/settings" element={<NaiDeskSettingsAdmin />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
